/* eslint-disable react-refresh/only-export-components */
import clsx from 'clsx'
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react'
import { fmtDscr } from '@/utils/liveMetrics'
import type { Figure, FigureTone } from '@/types/dashboards'
import { useDrillDown } from './useDrillDown'

/** Absent measurement. Never rendered as 0 — see design spec §7. */
export const NO_VALUE = '--'

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/** $1.24B / $184.0M / $12.4K — the scale executives read amounts at. */
function currencyScaled(amount: number): string {
  const sign = amount < 0 ? '-' : ''
  const abs = Math.abs(amount)
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(2)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

/**
 * A Figure as text.
 *
 * `pct` is percentage POINTS as sent — the dashboard API computes every
 * percentage through common.ratio_pct, which already multiplies by 100. It
 * deliberately does NOT run through liveMetrics' pctPts normalization: that
 * helper's `v <= 1 ? v * 100 : v` rule exists for the legacy loan pages, where
 * live records store fractions, and applied here it would multiply a genuine
 * 0.5% (1 of 200) by 100 again and print '50.0%'. `pctPts` is a
 * percentage-point movement — 2.4 pts is 2.4 pts.
 */
export function formatFigure(figure: Figure): string {
  const { value, format } = figure
  if (value === null || value === undefined) return NO_VALUE

  if (format === 'text') {
    const text = String(value).trim()
    return text || NO_VALUE
  }

  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return NO_VALUE

  switch (format) {
    case 'currency':
      return CURRENCY.format(numeric)
    case 'currencyM':
      return currencyScaled(numeric)
    case 'count':
      return numeric.toLocaleString('en-US')
    case 'pct':
      return `${numeric.toFixed(1)}%`
    case 'pctPts':
      return `${numeric > 0 ? '+' : ''}${numeric.toFixed(1)} pts`
    case 'ratio':
      return fmtDscr(numeric)
    default: {
      // Compile-time exhaustiveness only. Returning `_exhaustive` would render
      // the format NAME as the value whenever a caller widens FigureFormat or
      // passes a type through from the wire, so fall back to the raw value.
      const _exhaustive: never = format
      void _exhaustive
      return String(value)
    }
  }
}

/**
 * A Figure as a board prints it: one compact institutional money format
 * everywhere on a board ($1.09B, $416.2M, $64.5M), whatever precision the
 * server sent. Exact dollars stay where an analyst reconciles — the drill
 * table (which formats through `formatFigure`) and the hover title
 * (`exactFigure`).
 */
export function displayFigure(figure: Figure): string {
  return figure.format === 'currency' ? formatFigure({ ...figure, format: 'currencyM' }) : formatFigure(figure)
}

/** The exact value for a hover title, when the printed one is rounded; null otherwise. */
export function exactFigure(figure: Figure): string | null {
  if (figure.format !== 'currency' && figure.format !== 'currencyM') return null
  const exact = formatFigure({ ...figure, format: 'currency' })
  return exact === NO_VALUE || exact === displayFigure(figure) ? null : exact
}

export function figureToneClass(tone?: FigureTone): string {
  switch (tone) {
    case 'positive':
      return 'text-emerald-400'
    case 'negative':
      return 'text-red-400'
    case 'warning':
      return 'text-amber-300'
    default:
      return 'text-stone-100'
  }
}

const DELTA_ICON = { up: ArrowUp, down: ArrowDown, flat: ArrowRight } as const

export function FigureDeltaBadge({ figure }: { figure: Figure }) {
  if (!figure.delta) return null
  const Icon = DELTA_ICON[figure.delta.direction]
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 text-[10px] leading-none',
        figure.delta.direction === 'flat' ? 'text-stone-500' : figureToneClass(figure.tone),
      )}
      title={figure.delta.label}
    >
      <Icon size={9} />
      {figure.delta.label}
    </span>
  )
}

interface FigureValueProps {
  figure: Figure
  className?: string
  /** Show the delta badge beside the value. */
  showDelta?: boolean
  align?: 'left' | 'right' | 'center'
}

/**
 * The value half of a Figure. Clickable when the Figure carries a `drill`;
 * otherwise plain text, so a non-drillable number never advertises an
 * interaction that does nothing.
 */
export default function FigureValue({
  figure,
  className,
  showDelta = false,
  align = 'left',
}: FigureValueProps) {
  const { open } = useDrillDown()
  const text = displayFigure(figure)
  const exact = exactFigure(figure)
  const isBlank = text === NO_VALUE

  const body = (
    <>
      {/* Keyed by the rendered text so the fade replays only when the value
          actually changes — never on a poll tick that returns the same
          figure. */}
      <span key={text} className={clsx('animate-number-fade', isBlank && 'text-stone-600')} title={exact ?? undefined}>
        {text}
      </span>
      {showDelta && <FigureDeltaBadge figure={figure} />}
    </>
  )

  const alignment =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'

  if (!figure.drill) {
    return (
      <span className={clsx('inline-flex items-baseline gap-1.5', alignment, className)}>
        {body}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => open(figure.drill)}
      title={`${figure.drill.title}${exact ? ` · ${exact}` : ''} — open records`}
      className={clsx(
        'inline-flex items-baseline gap-1.5 rounded transition-colors',
        'underline decoration-amber-400/25 decoration-dotted underline-offset-[3px]',
        'hover:decoration-amber-300/70 hover:text-amber-200',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50',
        alignment,
        className,
      )}
    >
      {body}
    </button>
  )
}
